import { css, html, LitElement, nothing, type TemplateResult } from "lit";
import { SignalWatcher } from "@lit-labs/signals";
import { customElement, property, state } from "lit/decorators.js";
import * as Styles from "../../styles.ts";
import { AppStore } from "../app-store.ts";
import { ulid } from "@std/ulid";
import "../dui/dialog/dui-dialog.ts";
import "../dui/dialog/dui-dialog-popup.ts";
import "../dui/button/dui-button.ts";
import "../dui/icon/dui-icon.ts";
import "../dui/input/dui-input.ts";
import "../dui/textarea/dui-textarea.ts";
import "../dui/toolbar/dui-toolbar.ts";
import "../dui/vstack/dui-vstack.ts";
import "../dui/field/dui-field.ts";
import "../dui/field/dui-field-error.ts";
import "../dui/field/dui-field-label.ts";

const styles = css`
  :host {
    display: block;
  }

  h1 {
    font-size: var(--font-size-xl);
    letter-spacing: var(--letter-spacing-tighter);
    font-weight: var(--font-weight-semibold);
    color: var(--foreground);
  }

  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-4);
    margin-top: var(--space-4);
  }
`;

@customElement("create-project-modal")
export class CreateProjectModalElement extends SignalWatcher(LitElement) {
  static override styles = [Styles.base, styles];

  @property({ attribute: false })
  accessor store: AppStore | undefined = undefined;

  @state()
  accessor #submitted = false;

  #onClose = (): void => {
    this.#submitted = false;
    this.store?.send({
      type: "toggle-create-project-modal",
      value: false,
    });
  };

  #onTitleInput = (event: CustomEvent<{ value: string }>): void => {
    this.store?.send({
      type: "set-create-project-modal-title",
      value: event.detail.value,
    });
  };

  #onDescriptionInput = (event: CustomEvent<{ value: string }>): void => {
    this.store?.send({
      type: "set-create-project-modal-description",
      value: event.detail.value,
    });
  };

  #onCreateProject = (): void => {
    if (!this.store) return;

    const title = this.store.get().createProjectModalTitle.trim();
    if (title.length === 0) {
      this.#submitted = true;
      return;
    }

    this.#submitted = false;
    this.store.send({
      type: "submit-create-project-modal",
      id: ulid(),
      title,
      description: this.store.get().createProjectModalDescription,
      accessToken: this.store.get().session?.access_token,
    });
  };

  #onOpenChange = (event: CustomEvent<{ open: boolean }>) => {
    if (!event.detail.open) {
      this.#submitted = false;
    }
    this.store?.send({
      type: "toggle-create-project-modal",
      value: event.detail.open,
    });
  };

  override render(): TemplateResult {
    const title = this.store?.get().createProjectModalTitle ?? "";
    const titleInvalid = this.#submitted && title.trim().length === 0;

    return html`
      <dui-dialog
        .open="${this.store?.get().showCreateProjectModal}"
        @dialog-open-change="${this.#onOpenChange}"
      >
        <dui-dialog-popup width="30rem">
          <dui-vstack>
            <dui-toolbar>
              <h1 slot="left">Create a new project</h1>
              <dui-button
                slot="right"
                variant="ghost"
                square
                @click="${this.#onClose}"
                ><dui-icon
                  icon="close"
                  color="var(--muted-foreground)"
                ></dui-icon
              ></dui-button>
            </dui-toolbar>

            <dui-field ?invalid="${titleInvalid}">
              <dui-field-label variant="secondary"
                >What are you exploring?</dui-field-label
              >
              <dui-input
                .value="${title}"
                placeholder="Name your project"
                required
                autofocus
                @input-change="${this.#onTitleInput}"
              ></dui-input>
              ${titleInvalid
                ? html`<dui-field-error>Name is required</dui-field-error>`
                : nothing}
            </dui-field>

            <dui-field>
              <dui-field-label variant="secondary"
                >What are some key project details?</dui-field-label
              >
              <dui-textarea
                .value="${this.store?.get().createProjectModalDescription ??
                ""}"
                placeholder="Describe the topic, scope, and goals of this project"
                resize="auto"
                rows="2"
                @textarea-change="${this.#onDescriptionInput}"
              ></dui-textarea>
            </dui-field>

            <dui-toolbar>
              <dui-button
                slot="right"
                @click="${this.#onClose}"
                variant="outline"
              >
                Cancel
              </dui-button>
              <dui-button
                variant="primary"
                slot="right"
                @click="${this.#onCreateProject}"
              >
                Create
              </dui-button>
            </dui-toolbar>
          </dui-vstack>
        </dui-dialog-popup>
      </dui-dialog>
    `;
  }
}
